import React from "react";
import { Button, Field } from "@fluentui/react-components";
import { Input } from "#root/components/Input";
import ImageUploader, { UploaderFile } from "#root/components/ImageUploader";
import ButtonGroup from "#root/components/ButtonGroup";
import { FAMILY_ASSISTANCE_ENUMS, COUNSELING_CONST } from "#root/utils/familyAssistance";
import { stripPhoneNumber, VALIDATION_MESSAGES, VALIDATION_PATTERNS } from "#root/utils/validation";
import "./styles.scss";

export interface FamilyAssistanceFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  lookingForFirstName: string;
  lookingForLastName: string;
  familyMembersCount: number;
  childrenCount?: number;
  counselorPreferences: number[];
  needsChildcare: boolean;
  hasDisabilities: boolean;
  specifyDisabilities: number[];
  needs: number[];
  additionalNeeds: string;
  familyAssistanceImage: UploaderFile | null;
  photoUrl?: string;
  photoId?: string;
}

interface FamilyAssistanceFormProps {
  formData: FamilyAssistanceFormData;
  onFieldChange: (name: string, value: any) => void;
  onSubmit: (formData: FamilyAssistanceFormData) => Promise<void>;
  submitButtonText: string;
  isSubmitting: boolean;
  incidentName?: string;
  onFileDelete?: (file: UploaderFile) => void;
  onCancel?: () => void;
  isEdit?: boolean;
}

const FamilyAssistanceForm: React.FC<FamilyAssistanceFormProps> = ({
  formData,
  onFieldChange,
  onSubmit,
  submitButtonText,
  isSubmitting,
  incidentName,
  onFileDelete,
  onCancel,
  isEdit,
}) => {

  const handleImageChange = (files: UploaderFile[]): void => {
    onFieldChange("familyAssistanceImage", files[0]);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // Strip spaces from phone number before submitting
    const updatedFormData = {
      ...formData,
      phoneNumber: stripPhoneNumber(formData.phoneNumber)
    };
    await onSubmit(updatedFormData);
  };

  const handleCounselorChange = (selected: number[]) => {
    const prevSelection = Array.isArray(formData.counselorPreferences)
      ? formData.counselorPreferences
      : [];

    if (!prevSelection.includes(2) && selected.includes(2)) {
      return onFieldChange("counselorPreferences", [2]);
    }

    if (prevSelection.includes(2) && selected.includes(2) && selected.length > 1) {
      return onFieldChange("counselorPreferences", selected.filter(v => v !== 2));
    }

    onFieldChange("counselorPreferences", selected);
  };

  return (
    <form autoComplete="off" onSubmit={handleSubmit} className={`family-assistance-form ${isSubmitting ? 'submitting' : ''}`}>

      {incidentName && (
        <Field label="Incident">
          <Input
            value={incidentName}
            readOnly
          />
        </Field>
      )}

      <Field label="Please enter your First Name" required>
        <Input
          name="firstName"
          value={formData.firstName}
          onChange={(e) => onFieldChange(e.target.name, e.target.value)}
          maxLength={100}
        />
      </Field>

      <Field label="Please enter your Last Name" required>
        <Input
          name="lastName"
          value={formData.lastName}
          onChange={(e) => onFieldChange(e.target.name, e.target.value)}
          maxLength={100}
        />
      </Field>

      <Field label="Your Photo">
        <div className="description">Please take a photo of yourself using the button below so staff can easier locate you if we have questions or require additional information.</div>
        <ImageUploader
          files={formData.familyAssistanceImage ? [formData.familyAssistanceImage] : []}
          onChange={handleImageChange}
          maxFiles={1}
          onFileDelete={onFileDelete}
        />
      </Field>

      <Field label="Please enter your email address" required>
        <Input
          name="email"
          type="email"
          value={formData.email}
          onChange={(e) => onFieldChange(e.target.name, e.target.value)}
        />
      </Field>

      <Field label="Please enter the best phone number to call or text information." required>
        <Input
          name="phoneNumber"
          type="tel"
          value={formData.phoneNumber}
          onChange={(e) => onFieldChange(e.target.name, e.target.value)}
          pattern={VALIDATION_PATTERNS.PHONE}
          title={VALIDATION_MESSAGES.PHONE}
        />
      </Field>

      <Field label="First Name of person you are looking for" required>
        <Input
          name="lookingForFirstName"
          value={formData.lookingForFirstName}
          onChange={(e) => onFieldChange(e.target.name, e.target.value)}
          maxLength={100}
        />
      </Field>

      <Field label="Last Name of person you are looking for" required>
        <Input
          name="lookingForLastName"
          value={formData.lookingForLastName}
          onChange={(e) => onFieldChange(e.target.name, e.target.value)}
          maxLength={100}
        />
      </Field>

      <Field label="How many family/friends are in the family reception center with you?" required>
        <ButtonGroup
          options={FAMILY_ASSISTANCE_ENUMS.FAMILY_COUNT.map(e => ({ label: e.label, value: e.value }))}
          value={formData.familyMembersCount}
          onChange={(value: number) => onFieldChange("familyMembersCount", value)}
          name="familyMembersCount"
        />
      </Field>

      <Field label="Would you like to speak with a counselor or chaplain?" required>
        <ButtonGroup
          options={COUNSELING_CONST.map((e) => ({ label: e.label, value: e.value }))}
          value={formData.counselorPreferences || []}
          onChange={handleCounselorChange}
          name="counselorPreferences"
          required
          multiple
        />
      </Field>

      <Field label="Do you need help with childcare while at the family reunification/ assistance center?" required>
        <ButtonGroup
          options={FAMILY_ASSISTANCE_ENUMS.YES_NO.map(e => ({ label: e.label, value: e.value }))}
          value={formData.needsChildcare}
          onChange={(value: boolean) => onFieldChange("needsChildcare", value)}
          name="needsChildcare"
        />
      </Field>

      {formData.needsChildcare === true && (
        <Field label="How many Children?" required>
          <ButtonGroup
            options={FAMILY_ASSISTANCE_ENUMS.FAMILY_COUNT.map(e => ({ label: e.label, value: e.value }))}
            value={formData.childrenCount}
            onChange={(value: number) => onFieldChange("childrenCount", value)}
            name="childrenCount"
          />
        </Field>
      )}


      <Field label="Do you or any family members with you have any disabilities that may require assistance while at the family reunification center?" required>
        <ButtonGroup
          options={FAMILY_ASSISTANCE_ENUMS.YES_NO.map(e => ({ label: e.label, value: e.value }))}
          value={formData.hasDisabilities}
          onChange={(value: boolean) => onFieldChange("hasDisabilities", value)}
          name="hasDisabilities"
        />
      </Field>

      {formData.hasDisabilities === true && (
        <Field label="Please specify what disabilities" required>
          <ButtonGroup
            options={FAMILY_ASSISTANCE_ENUMS.DISABILITIES.map(e => ({ label: e.label, value: e.value }))}
            value={formData.specifyDisabilities}
            onChange={(value: number[]) => onFieldChange("specifyDisabilities", value)}
            name="specifyDisabilities"
            multiple
          />
        </Field>
      )}

      <Field label="Do you have any of the following needs?">
        <ButtonGroup
          options={FAMILY_ASSISTANCE_ENUMS.NEEDS.map(e => ({ label: e.label, value: e.value }))}
          value={formData.needs}
          onChange={(value: number[]) => onFieldChange("needs", value)}
          name="needs"
          multiple
        />
      </Field>

      <Field label="Additional Needs">
        <Input
          name="additionalNeeds"
          value={formData.additionalNeeds}
          onChange={(e) => onFieldChange(e.target.name, e.target.value)}
        />
      </Field>

      <div className="form-actions">
        {isEdit ? (
          <>
            <Button type="submit" appearance="primary" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : submitButtonText}
            </Button>
            <Button appearance="secondary" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          </>
        ) : (
          <Button type="submit" appearance="primary" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : submitButtonText}
          </Button>
        )}
      </div>
    </form>
  );
};

export default FamilyAssistanceForm;
